import GavelRoundedIcon from "@mui/icons-material/GavelRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import PrivacyTipRoundedIcon from "@mui/icons-material/PrivacyTipRounded";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import { Link as RouterLink, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { acceptLegalDocuments, getCurrentLegalDocuments } from "../api/legal";
import { getApiError } from "../api/errors";
import AppLogo from "../components/AppLogo";
import { useAuth } from "../context/AuthContext";

export default function LegalAcceptance() {
  const {
    selectedWorkspace,
    workspaceOperational,
    legalStatus,
    legalStatusLoading,
    refreshLegalStatus,
    platformRole,
    logout,
  } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [documents, setDocuments] = useState(null);
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    getCurrentLegalDocuments()
      .then((response) => {
        if (active) setDocuments(response.data);
      })
      .catch((requestError) => {
        if (active) setError(getApiError(requestError, "Could not load the current ABOS legal documents."));
      });
    return () => {
      active = false;
    };
  }, []);

  const destination = useMemo(() => {
    const requested = location.state?.from?.pathname;
    if (requested && requested !== "/legal/accept") return requested;
    if (platformRole && !selectedWorkspace) return "/platform";
    return workspaceOperational ? "/dashboard" : "/onboarding";
  }, [location.state, platformRole, selectedWorkspace, workspaceOperational]);

  if (!selectedWorkspace && !legalStatusLoading) {
    return <Navigate to={platformRole ? "/platform" : "/settings"} replace />;
  }

  if (legalStatus && !legalStatus.required && !legalStatusLoading) {
    return <Navigate to={destination} replace />;
  }

  const handleAccept = async () => {
    if (!checked || !documents || !selectedWorkspace) return;
    setSubmitting(true);
    setError("");
    try {
      const payload = {
        accepted: true,
        terms_version: documents.terms.version,
        privacy_version: documents.privacy.version,
        terms_hash: documents.terms.content_hash,
        privacy_hash: documents.privacy.content_hash,
      };
      await acceptLegalDocuments(selectedWorkspace.public_id, payload);
      await refreshLegalStatus(selectedWorkspace.public_id);
      navigate(destination, { replace: true });
    } catch (requestError) {
      setChecked(false);
      setError(getApiError(requestError, "Could not record your acceptance. Please review the current documents and try again."));
      try {
        await refreshLegalStatus(selectedWorkspace.public_id);
      } catch (_) {
        // Preserve the original acceptance error for the user.
      }
    } finally {
      setSubmitting(false);
    }
  };

  const loading = legalStatusLoading || !documents;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        p: { xs: 1.5, sm: 3 },
        bgcolor: "#f8fafc",
        backgroundImage:
          "radial-gradient(circle at 10% 10%, rgba(37,99,235,.10), transparent 30%), radial-gradient(circle at 90% 12%, rgba(20,184,166,.10), transparent 28%)",
      }}
    >
      <Card sx={{ width: "100%", maxWidth: 760, borderRadius: 5, border: "1px solid #dbe4ef", boxShadow: "0 30px 80px rgba(15,23,42,.12)" }}>
        <CardContent sx={{ p: { xs: 3, sm: 5 } }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={2}>
            <AppLogo />
            <Button color="inherit" size="small" onClick={logout}>
              Sign out
            </Button>
          </Stack>

          {loading ? (
            <Box sx={{ minHeight: 360, display: "grid", placeItems: "center" }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Box sx={{ width: 52, height: 52, borderRadius: 3, display: "grid", placeItems: "center", bgcolor: "#eff6ff", color: "#2563eb", mt: 5 }}>
                <GavelRoundedIcon />
              </Box>
              <Typography variant="h4" sx={{ mt: 2 }}>
                Review ABOS terms before continuing
              </Typography>
              <Typography color="text.secondary" sx={{ mt: 1.2, lineHeight: 1.75 }}>
                The current Store Owner for <strong>{selectedWorkspace?.name}</strong> must accept the current Terms of Service and Privacy Policy before protected store operations are available.
              </Typography>

              {error && <Alert severity="error" sx={{ mt: 2.5 }}>{error}</Alert>}

              <Stack spacing={1.5} sx={{ mt: 3 }}>
                <Box sx={{ p: 2, borderRadius: 3, border: "1px solid #dbeafe", bgcolor: "#f8fbff" }}>
                  <Stack direction="row" spacing={1.25} alignItems="center">
                    <LockRoundedIcon color="primary" />
                    <Box sx={{ flex: 1 }}>
                      <Typography fontWeight={900}>ABOS Terms of Service</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Version {documents.terms.version} · Effective {documents.terms.effective_date}
                      </Typography>
                    </Box>
                    <Link component={RouterLink} to="/terms" target="_blank" rel="noopener" fontWeight={850}>
                      Read
                    </Link>
                  </Stack>
                </Box>
                <Box sx={{ p: 2, borderRadius: 3, border: "1px solid #d1fae5", bgcolor: "#f7fffb" }}>
                  <Stack direction="row" spacing={1.25} alignItems="center">
                    <PrivacyTipRoundedIcon sx={{ color: "#059669" }} />
                    <Box sx={{ flex: 1 }}>
                      <Typography fontWeight={900}>ABOS Privacy Policy</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Version {documents.privacy.version} · Effective {documents.privacy.effective_date}
                      </Typography>
                    </Box>
                    <Link component={RouterLink} to="/privacy" target="_blank" rel="noopener" fontWeight={850}>
                      Read
                    </Link>
                  </Stack>
                </Box>
              </Stack>

              <FormControlLabel
                sx={{ alignItems: "flex-start", mt: 3, mx: 0 }}
                control={<Checkbox checked={checked} onChange={(event) => setChecked(event.target.checked)} sx={{ mt: -0.35, ml: -1 }} />}
                label={
                  <Typography sx={{ lineHeight: 1.6 }}>
                    I confirm that I am authorized to act for this business and that I have read and agree to the current ABOS Terms of Service and Privacy Policy.
                  </Typography>
                }
              />

              <Button
                fullWidth
                variant="contained"
                size="large"
                disabled={!checked || submitting}
                onClick={handleAccept}
                sx={{ mt: 2.5, py: 1.25 }}
              >
                {submitting ? <CircularProgress size={23} color="inherit" /> : "Agree & Continue"}
              </Button>

              <Typography variant="caption" color="text.secondary" display="block" textAlign="center" sx={{ mt: 2 }}>
                Acceptance is recorded with the document versions, content fingerprints, timestamp, account, workspace, and security request metadata.
              </Typography>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
